import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, DownloadCloud, FileText, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { formatTimestamp } from "@/utils/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const kbLabel = (bytes) => (bytes >= 1024 * 1024
  ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
  : `${(bytes / 1024).toFixed(1)} KB`);

const shortType = (contentType = "") => {
  const map = {
    "application/pdf": "PDF",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
    "application/vnd.ms-excel": "XLS",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
    "application/vnd.ms-powerpoint": "PPT",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
    "text/csv": "CSV",
    "text/plain": "TXT",
    "application/zip": "ZIP",
  };
  return map[contentType] || contentType.split("/").pop()?.toUpperCase() || "FILE";
};

export default function ProjectDocumentsPanel({ projectId, defaultOpen = false }) {
  const { isFeatureEnabled, hasPermission } = useAuth();
  const enabled = isFeatureEnabled("project_documents");
  const canRead = hasPermission("project_documents:read");
  const canManage = hasPermission("project_documents:manage");
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(defaultOpen);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const inputRef = useRef(null);

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadZip = async () => {
    if (checkedIds.size === 0) return;
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/documents/bulk-zip`,
        { document_ids: Array.from(checkedIds) },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `project-${projectId}-documents.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setCheckedIds(new Set());
    } catch (error) {
      toast.error(error?.response?.data?.detail || "ZIP download failed.");
    }
  };

  const loadCount = useCallback(async () => {
    if (!enabled || !canRead) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/documents/count`);
      setCount(res.data.count || 0);
    } catch (error) {
      // ignore
    }
  }, [enabled, canRead, projectId]);

  const loadList = useCallback(async () => {
    if (!enabled || !canRead) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/documents`);
      setDocuments(res.data.documents || []);
      setCount((res.data.documents || []).length);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load documents.");
    } finally {
      setLoading(false);
    }
  }, [enabled, canRead, projectId]);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await axios.post(`${API}/projects/${projectId}/documents`, form);
      }
      toast.success(`Uploaded ${files.length} document${files.length > 1 ? "s" : ""}.`);
      await loadList();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove ${doc.original_filename}? It stays recoverable for 30 days.`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/documents/${doc.id}`);
      toast.success("Document removed.");
      await loadList();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!enabled || !canRead) return null;

  return (
    <div className="project-documents" data-testid="project-documents-root">
      <button
        className="table-edit-button"
        data-testid="project-documents-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
        type="button"
      >
        <FileText size={14} />
        Documents ({count})
      </button>

      {open && (
        <section
          data-testid="project-documents-panel"
          style={{
            marginTop: "0.65rem",
            padding: "0.75rem",
            border: "1px solid #e2e5ea",
            borderRadius: "8px",
            background: "#fafbfc",
          }}
        >
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.6rem" }}>
            {canManage && (
              <>
                <input
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,image/jpeg,image/png,image/webp"
                  data-testid="project-documents-input"
                  multiple
                  onChange={upload}
                  ref={inputRef}
                  style={{ display: "none" }}
                  type="file"
                />
                <button
                  className="table-edit-button"
                  data-testid="project-documents-upload"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                  type="button"
                >
                  <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
                </button>
              </>
            )}
            {checkedIds.size > 0 && (
              <button
                className="table-edit-button"
                data-testid="project-documents-download-zip"
                onClick={downloadZip}
                type="button"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              >
                <DownloadCloud size={13} /> Download ZIP ({checkedIds.size})
              </button>
            )}
            <button
              data-testid="project-documents-close"
              onClick={() => setOpen(false)}
              style={{ marginLeft: "auto" }}
              type="button"
            >
              Close
            </button>
          </div>

          {loading ? (
            <p data-testid="project-documents-loading">Loading…</p>
          ) : documents.length === 0 ? (
            <p data-testid="project-documents-empty" style={{ color: "#64748b" }}>
              No documents yet.
            </p>
          ) : (
            <ul
              data-testid="project-documents-list"
              style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "0.4rem" }}
            >
              {documents.map((doc) => {
                const isChecked = checkedIds.has(doc.id);
                return (
                <li
                  data-testid={`project-document-row-${doc.id}`}
                  key={doc.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.5rem 0.6rem",
                    border: isChecked ? "2px solid #2563eb" : "1px solid #e5e7eb",
                    borderRadius: "6px",
                    background: "#fff",
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={`Select ${doc.original_filename}`}
                    data-testid={`project-document-check-${doc.id}`}
                    checked={isChecked}
                    onChange={() => toggleChecked(doc.id)}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                  <span
                    aria-hidden="true"
                    style={{
                      minWidth: "42px",
                      textAlign: "center",
                      padding: "3px 6px",
                      background: "#eef2ff",
                      borderRadius: "4px",
                      color: "#3730a3",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    {shortType(doc.content_type)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontWeight: 600,
                      }}
                      title={doc.original_filename}
                    >
                      {doc.original_filename}
                    </div>
                    <small style={{ color: "#64748b" }}>
                      {kbLabel(doc.byte_size)} · {formatTimestamp(doc.uploaded_at)}
                      {" · "}by {doc.uploaded_by_name_snapshot}
                    </small>
                  </div>
                  <a
                    className="table-edit-button"
                    data-testid={`project-document-download-${doc.id}`}
                    download={doc.original_filename}
                    href={`${API}/projects/${projectId}/documents/${doc.id}/download`}
                    style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                  >
                    <Download size={13} /> Download
                  </a>
                  {canManage && (
                    <button
                      className="table-edit-button"
                      data-testid={`project-document-delete-${doc.id}`}
                      onClick={() => remove(doc)}
                      style={{ color: "#dc2626" }}
                      type="button"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
